<template>
  <div class="insights-display">
    <div
      v-if="!hasDemand"
      class="text-muted"
    >
      No demand in <strong>{{ insights.categoryName }}</strong> | <strong>{{ insights.languageName }}</strong>
    </div>
    <div v-else>
      <div
        class="insights-header mb-2"
        role="button"
        @click="isTableVisible = !isTableVisible"
      >
        <font-awesome-icon
          :icon="isTableVisible ? 'chevron-down' : 'chevron-right'"
          :class="['mr-2', { 'text-success': !isTableVisible }]"
        />
        Demand in <strong>{{ insights.categoryName }}</strong> | <strong>{{ insights.languageName }}</strong>
      </div>
      <b-collapse v-model="isTableVisible">
        <b-table-simple
          small
          bordered
          class="insights-table mb-0"
        >
          <b-thead>
            <b-tr>
              <b-th>Location</b-th>
              <b-th>Hours</b-th>
              <b-th>Traffic Sources</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr
              v-for="(entry, index) in scheduleEntries"
              :key="index"
            >
              <b-td>{{ entry.location }}</b-td>
              <b-td>{{ entry.hours }}</b-td>
              <b-td>{{ entry.trafficSources }}</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-collapse>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InsightsDisplay',

  props: {
    insights: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      isTableVisible: false
    }
  },

  computed: {
    hasDemand() {
      const schedule = this.insights?.schedule
      if (!schedule?.length) return false
      return schedule.some(entry => entry.trafficSourceNames?.length > 0)
    },

    scheduleEntries() {
      const schedule = this.insights?.schedule || []
      return schedule
        .map(entry => this.formatScheduleEntry(entry))
        .filter(Boolean)
    }
  },

  methods: {
    formatScheduleEntry(entry) {
      const { isAllHours, dailySchedule, location, trafficSourceNames } = entry

      if (!trafficSourceNames?.length) {
        return null
      }

      const locationText = this.formatLocation(location)
      if (!locationText) {
        return null
      }

      const hoursText = isAllHours ? '24/7' : this.formatDailySchedule(dailySchedule)
      const uniqueTrafficSources = [...new Set(trafficSourceNames)]

      return {
        location: locationText,
        hours: hoursText || 'No schedule',
        trafficSources: uniqueTrafficSources.join(', ')
      }
    },

    formatLocation(location) {
      if (location?.isNationwide) {
        return 'Nationwide'
      }
      if (location?.states?.length) {
        return location.states.join(', ')
      }
      return null
    },

    formatDailySchedule(dailySchedule) {
      if (!dailySchedule?.length) return null

      const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

      // Check if all days are closed
      const openDayIndices = dailySchedule
        .map((d, i) => (!d.isClosed ? i : -1))
        .filter(i => i !== -1)

      if (!openDayIndices.length) return 'Closed'

      // Get time range for each day
      const getTimeRange = (d) => {
        if (d.isClosed) return null
        return `${this.formatTime(d.openTime)} - ${this.formatTime(d.closeTime)}`
      }

      const timeRanges = dailySchedule.map(getTimeRange)
      const uniqueOpenRanges = [...new Set(timeRanges.filter(Boolean))]

      // Case 1: All days have the same schedule (all open with same hours)
      if (openDayIndices.length === 7 && uniqueOpenRanges.length === 1) {
        return `Mon-Sun: ${uniqueOpenRanges[0]}`
      }

      // Case 2: Some days closed, but all open days have the same hours
      if (uniqueOpenRanges.length === 1) {
        const closedDayIndices = dailySchedule
          .map((d, i) => (d.isClosed ? i : -1))
          .filter(i => i !== -1)

        const closedDayNames = closedDayIndices.map(i => dayNames[i])
        return `${uniqueOpenRanges[0]} except ${closedDayNames.join(', ')}`
      }

      // Case 3: Different hours across days - show per day
      const daySchedules = []
      let i = 0

      while (i < 7) {
        if (dailySchedule[i].isClosed) {
          i++
          continue
        }

        const currentRange = timeRanges[i]
        let j = i

        // Find consecutive days with the same schedule
        while (j < 7 && timeRanges[j] === currentRange) {
          j++
        }

        const startDay = dayNames[i]
        const endDay = dayNames[j - 1]

        if (i === j - 1) {
          daySchedules.push(`${startDay}: ${currentRange}`)
        } else {
          daySchedules.push(`${startDay}-${endDay}: ${currentRange}`)
        }

        i = j
      }

      return daySchedules.join(', ')
    },

    formatTime(time) {
      if (!time) return ''
      const hour = time.hour % 12 || 12
      const minute = time.minute.toString().padStart(2, '0')
      const period = time.hour >= 12 ? 'PM' : 'AM'
      return `${hour}:${minute} ${period}`
    }
  }
}
</script>

<style lang="scss" scoped>
.insights-display {
  font-size: 0.9rem;
}

.insights-header {
  cursor: pointer;
  user-select: none;

  &:hover {
    color: var(--primary);
  }
}

.insights-table {
  font-size: 0.85rem;

  th {
    font-weight: 600;
    font-size: 0.9rem;
  }
}
</style>
